import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import type { RootState, AppDispatch } from "../../../app/store";
import { setCategory, setTag } from "../productSlice";

export const useProductsUrlSync = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { category: currentCategoryFilters, tag: currentTagFilters } =
    useSelector((state: RootState) => state.product.filters);

  const [urlSyncComplete, setUrlSyncComplete] = useState(false);

  // Effect for initial URL read and Redux state update
  useEffect(() => {
    const categoriesFromUrl = searchParams.get("cat_ids");
    const tagsFromUrl = searchParams.get("tag_ids");

    // Note: searchParams.get() returns the decoded value.
    // For example, if URL is "?cat_ids=21%2C23", categoriesFromUrl will be "21,23".
    // The .split(",") method then correctly parses this string.

    if (categoriesFromUrl) {
      const categoryIds = categoriesFromUrl
        .split(",")
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !isNaN(id));
      if (categoryIds.length > 0) {
        dispatch(setCategory({ replace: categoryIds }));
      }
    }

    if (tagsFromUrl) {
      const tagIds = tagsFromUrl
        .split(",")
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !isNaN(id));
      if (tagIds.length > 0) {
        dispatch(setTag({ replace: tagIds }));
      }
    }
    // If no params were in URL, or if they were invalid/empty,
    // consider sync complete for those.
    // The main goal is to set urlSyncComplete to true after this initial pass.
    setUrlSyncComplete(true);
  }, [dispatch, searchParams]); // Only run on mount and if searchParams object identity changes (rarely)

  // Effect for updating URL when Redux filter state changes
  useEffect(() => {
    if (!urlSyncComplete) {
      return; // Don't update URL until initial sync from URL to Redux is done
    }

    const newParams = new URLSearchParams(searchParams);

    if (currentCategoryFilters.length > 0) {
      // Joining with a comma creates a string like "21,23".
      // setSearchParams will then URL-encode the comma, resulting in "21%2C23" in the URL.
      newParams.set("cat_ids", currentCategoryFilters.join(","));
    } else {
      newParams.delete("cat_ids");
    }

    if (currentTagFilters.length > 0) {
      // Similarly, for tags, "24,25" becomes "24%2C25" in the URL.
      newParams.set("tag_ids", currentTagFilters.join(","));
    } else {
      newParams.delete("tag_ids");
    }

    // Only update if params actually changed to avoid unnecessary history entries
    if (newParams.toString() !== searchParams.toString()) {
      setSearchParams(newParams, { replace: true });
    }
  }, [
    currentCategoryFilters,
    currentTagFilters,
    urlSyncComplete,
    setSearchParams,
    searchParams, // Include searchParams to compare against newParams
  ]);

  return { urlSyncComplete };
};
